import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { resolveMediaUrl } from "@/lib/api";
import { MapPin, Briefcase, Clock, Plus, Check } from "lucide-react";
import { useBasket, BASKET_MAX } from "@/context/BasketContext";

const STATUS_STYLES = {
  Available: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Booked: "bg-amber-50 text-amber-700 border-amber-200",
  Interviewed: "bg-sky-50 text-sky-700 border-sky-200",
  Selected: "bg-zinc-900 text-white border-zinc-900",
  Processing: "bg-violet-50 text-violet-700 border-violet-200",
  Departed: "bg-zinc-100 text-zinc-800 border-zinc-200",
  Inactive: "bg-zinc-100 text-zinc-400 border-zinc-200",
};

/**
 * Responsive candidate card.
 * - Mobile (< sm): compact horizontal layout — square photo + info + full-width CTAs.
 *   Optimised for employers scanning many candidates on a phone.
 * - Desktop (≥ sm): traditional vertical card with 4:5 photo & floating name plate.
 */
export default function CandidateCard({ candidate, showBookBtn = true }) {
  const isSelected = candidate.status === "Selected";
  const statusCls = STATUS_STYLES[candidate.status] || "bg-zinc-100 text-zinc-950 border-zinc-200";
  const basket = useBasket();
  const inBasket = basket.has(candidate.id);
  const toggleBasket = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSelected) return;
    if (!inBasket && basket.isFull) {
      toast.error(`Basket full — max ${BASKET_MAX} candidates. Remove one to add another.`);
      return;
    }
    const res = basket.toggle(candidate);
    if (res.didAdd) toast.success(`${candidate.name} added to booking basket (${basket.count + 1}/${BASKET_MAX})`);
    else toast(`${candidate.name} removed from basket`);
  };
  // Resolve photo URL through the /api gateway; null when candidate has no photo.
  const photoResolved = candidate.photo_url ? resolveMediaUrl(candidate.photo_url) : null;
  // Initials avatar fallback (up to 2 letters from the candidate name).
  const initials = (candidate.name || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className="group bg-white rounded-2xl sm:rounded-3xl border border-zinc-200 overflow-hidden card-lift"
      data-testid={`candidate-card-${candidate.id}`}
    >
      {/* MOBILE LAYOUT (< sm) */}
      <div className="sm:hidden flex flex-col">
        <div className="flex gap-3 p-3">
          <div className="relative w-28 h-32 shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-[#d4a017]/20 to-[#b8860b]/35">
            {photoResolved && (
              <img
                src={photoResolved}
                alt={candidate.name}
                className="w-full h-full object-cover object-top"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
              />
            )}
            <div
              className="absolute inset-0 items-center justify-center font-serif text-[#8a5f07] text-2xl"
              style={{ display: photoResolved ? "none" : "flex" }}
            >{initials}</div>
            <div className={`absolute top-1.5 left-1.5 text-[9px] px-1.5 py-0.5 rounded-full border font-semibold ${statusCls}`}>
              {candidate.status}
            </div>
          </div>
          <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
            <div>
              <div className="font-serif text-lg leading-tight text-zinc-950 truncate">{candidate.name}</div>
              <div className="text-[11px] text-zinc-800 mt-0.5">{candidate.age} yrs · {candidate.nationality}</div>
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-1.5 text-[11px] text-zinc-950 min-w-0">
                  <Briefcase size={11} className="text-[#b8860b] shrink-0" />
                  <span className="truncate">
                    {(candidate.roles && candidate.roles.length > 0)
                      ? candidate.roles.slice(0, 3).join(", ") + (candidate.roles.length > 3 ? `  +${candidate.roles.length - 3}` : "")
                      : candidate.role}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-zinc-950 truncate">
                  <MapPin size={11} className="text-[#b8860b] shrink-0" />
                  <span className="truncate">{candidate.country_name || "—"}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-zinc-950">
                  <Clock size={11} className="text-[#b8860b] shrink-0" />
                  <span className="text-[#b8860b] font-semibold">{candidate.experience_years} yrs</span>
                  <span className="text-zinc-800">experience</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-2 gap-y-1 mt-1 text-[10px] text-zinc-700">
              {candidate.religion && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900 font-medium">
                  {candidate.religion}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(candidate.skills || []).slice(0, 2).map((s) => (
                <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-950 border border-zinc-200">
                  {s}
                </span>
              ))}
              {(candidate.skills || []).length > 2 && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-50 text-zinc-800 border border-zinc-200">
                  +{candidate.skills.length - 2}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 px-3 pb-3">
          <Link
            to={`/candidates/${candidate.id}`}
            className="flex-1 text-center text-xs py-2 rounded-full border border-zinc-950 text-zinc-950 active:bg-zinc-950 active:text-white transition-colors font-medium"
            data-testid={`view-biodata-${candidate.id}`}
          >
            View Biodata
          </Link>
          {showBookBtn && !isSelected && (
            <button
              type="button"
              onClick={toggleBasket}
              className={
                "flex-1 text-center text-xs py-2 rounded-full transition-colors font-semibold inline-flex items-center justify-center gap-1 " +
                (inBasket
                  ? "bg-emerald-600 text-white active:bg-emerald-700"
                  : "bg-[#b8860b] text-zinc-950 active:bg-[#d4a017]")
              }
              data-testid={`basket-toggle-${candidate.id}`}
              aria-pressed={inBasket}
            >
              {inBasket ? <><Check size={12} /> In Basket</> : <><Plus size={12} /> Add to booking</>}
            </button>
          )}
          {isSelected && (
            <div className="flex-1 text-center text-xs py-2 rounded-full bg-zinc-900 text-white">
              Already Selected
            </div>
          )}
        </div>
      </div>

      {/* DESKTOP LAYOUT (≥ sm) */}
      <div className="hidden sm:block">
        <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-br from-[#d4a017]/20 to-[#b8860b]/35">
          {photoResolved && (
            <img
              src={photoResolved}
              alt={candidate.name}
              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
            />
          )}
          <div
            className="absolute inset-0 items-center justify-center font-serif text-[#8a5f07] text-7xl"
            style={{ display: photoResolved ? "none" : "flex" }}
          >{initials}</div>
          <div className={`absolute top-4 right-4 text-xs px-3 py-1 rounded-full border ${statusCls}`}>
            {candidate.status}
          </div>
          <div className="absolute bottom-4 left-4 right-4 backdrop-blur-xl bg-white/80 border border-white/60 rounded-2xl px-4 py-3">
            <div className="font-serif text-xl leading-tight text-zinc-950">{candidate.name}</div>
            <div className="text-xs text-zinc-900 mt-0.5">{candidate.age} yrs · {candidate.nationality}</div>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 flex-wrap text-xs text-zinc-900 mb-3">
            {(candidate.roles && candidate.roles.length > 0 ? candidate.roles : [candidate.role]).slice(0, 3).map((r) => (
              <span key={r} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#b8860b]/10 border border-[#b8860b]/30 text-[#8b6508] font-medium">
                <Briefcase size={11} className="text-[#b8860b]" /> {r}
              </span>
            ))}
            {(candidate.roles || []).length > 3 && (
              <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-700">+{candidate.roles.length - 3}</span>
            )}
            <span className="inline-flex items-center gap-1"><MapPin size={12} className="text-[#b8860b]" />{candidate.country_name || "—"}</span>
            {candidate.religion && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900 font-medium text-[10.5px]">
                {candidate.religion}
              </span>
            )}
          </div>
          <div className="text-sm text-zinc-950 mb-4">
            <span className="text-[#b8860b] font-semibold">{candidate.experience_years} yrs</span> experience
          </div>
          <div className="flex flex-wrap gap-1.5 mb-5 min-h-[52px]">
            {(candidate.skills || []).slice(0, 4).map((s) => (
              <span key={s} className="text-[11px] px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-950 border border-zinc-200">
                {s}
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Link
              to={`/candidates/${candidate.id}`}
              className="flex-1 text-center text-sm py-2.5 rounded-full border border-zinc-950 text-zinc-950 hover:bg-zinc-950 hover:text-white transition-colors"
            >
              View Biodata
            </Link>
            {showBookBtn && !isSelected && (
              <button
                type="button"
                onClick={toggleBasket}
                className={
                  "flex-1 text-center text-sm py-2.5 rounded-full transition-colors font-semibold inline-flex items-center justify-center gap-1.5 " +
                  (inBasket
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "bg-[#b8860b] text-zinc-950 hover:bg-[#d4a017]")
                }
                data-testid={`basket-toggle-${candidate.id}`}
                aria-pressed={inBasket}
              >
                {inBasket ? <><Check size={14} /> In Basket</> : <><Plus size={14} /> Add to booking</>}
              </button>
            )}
            {isSelected && (
              <div className="flex-1 text-center text-sm py-2.5 rounded-full bg-zinc-900 text-white cursor-not-allowed">
                Already Selected
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
